import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import ClientTimeline from "@/components/ClientTimeline";
import { GradeBadge, StatusBadge, RagBadge } from "@/components/Badges";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend,
} from "recharts";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Zap, ExternalLink } from "lucide-react";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })
    .format(n || 0);

function KpiTrendGrid({ kpiTrends }) {
  const [showAll, setShowAll] = useState(false);
  const INITIAL = 4;
  const visible = showAll ? kpiTrends : kpiTrends.slice(0, INITIAL);
  const hidden = Math.max(0, kpiTrends.length - INITIAL);

  if (kpiTrends.length === 0) {
    return (
      <div className="mptg-card text-slate-400 text-sm text-center py-10" data-testid="kpi-trends-empty">
        No KPIs defined.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="kpi-trends">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {visible.map(({ kpi, actuals }) => (
          <div key={kpi.id} className="mptg-card" data-testid={`kpi-trend-${kpi.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="mptg-overline">{kpi.unit}</div>
                <div className="font-heading font-bold text-lg tracking-tight">{kpi.name}</div>
              </div>
              <span className="text-xs text-slate-500 uppercase tracking-wider">
                {kpi.direction === "higher_is_better" ? "↑ better" : "↓ better"}
              </span>
            </div>
            <div className="h-44 mt-3">
              {actuals.length === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">No actuals logged</div>
              ) : (
                <ResponsiveContainer>
                  <LineChart data={actuals}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip contentStyle={{ background: "#1B2A4A", border: 0, color: "#fff", fontSize: 12 }} />
                    <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" name="Actual" dataKey="actual_value" stroke="#2E86C1" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" name="Benchmark" dataKey="benchmark_value_at_time" stroke="#94A3B8" strokeDasharray="4 4" strokeWidth={1.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        ))}
      </div>
      {hidden > 0 && (
        <button
          onClick={() => setShowAll((v) => !v)}
          className="text-xs text-[#2E86C1] hover:underline font-semibold"
          data-testid="toggle-kpi-trends"
        >
          {showAll ? `Collapse — show only top ${INITIAL}` : `Show all KPIs (+${hidden})`}
        </button>
      )}
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const initialTab = ["overview", "timeline", "campaigns", "billing", "reviews", "documents", "profitability", "intel"].includes(searchParams.get("tab"))
    ? searchParams.get("tab") : "timeline";
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [billing, setBilling] = useState([]);
  const [intel, setIntel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);
  const isAdmin = user?.role === "crm_manager";
  const isBM = user?.role === "branch_manager";
  const canSweep = ["crm_manager", "coo_ceo"].includes(user?.role);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [d, c, r, b, i] = await Promise.all([
        api.get(`/dashboard/client360/${id}`),
        api.get(`/contracts`, { params: { client_id: id } }),
        api.get(`/reviews`, { params: { client_id: id } }),
        api.get(`/billing`, { params: { client_id: id, page_size: 50 } }),
        api.get(`/intelligence/client/${id}`, { params: { limit: 10 } }),
      ]);
      setData(d.data); setContracts(c.data); setReviews(r.data); setBilling(b.data.items); setIntel(i.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !data) return <div className="p-8 text-slate-500">Loading client…</div>;

  const { client, summary, campaigns, billing_last_6, latest_review, kpi_trends } = data;

  const sweepNow = async () => {
    setSweeping(true);
    try {
      const { data: r } = await api.post(`/intelligence/client/${id}/sweep`);
      toast.success(`Sweep complete · ${r.inserted} items${r.adverse ? ` · ${r.adverse} adverse` : ""}`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSweeping(false); }
  };

  return (
    <div className="p-6 md:p-8 space-y-5" data-testid="client-360">
      {/* Header */}
      <div className="mptg-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mptg-overline">{client.industry || "Client"}</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight mt-1" data-testid="client-name">{client.name}</h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <StatusBadge status={client.status} />
              <GradeBadge grade={summary.latest_grade} />
              <span className="text-xs text-slate-500">{client.hq_location}</span>
              {summary.contract_days_remaining != null && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-md ${
                  summary.contract_days_remaining <= 30 ? "badge-red" :
                  summary.contract_days_remaining <= 60 ? "badge-amber" : "badge-blue"
                }`}>
                  Contract: {summary.contract_days_remaining} days
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-2">
              Owner: <span className="font-medium text-slate-700">{client.relationship_owner?.name || "-"}</span> ·
              Contact: <span className="text-slate-700">{client.primary_contact_name}</span> ·
              <span className="ml-1">{client.primary_contact_email}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" className="border-slate-200">
              <Link to={`/reviews/new?client_id=${client.id}`} data-testid="record-mbr-btn">Record MBR / QBR</Link>
            </Button>
            <Button asChild variant="outline" className="border-slate-200">
              <Link to={`/billing?client_id=${client.id}`} data-testid="add-billing-btn">Add Billing</Link>
            </Button>
            {canSweep && (
              <Button onClick={sweepNow} disabled={sweeping} className="bg-[#1B2A4A] hover:bg-[#243656] text-white" data-testid="sweep-client-btn">
                <Zap className="w-4 h-4 mr-1" /> {sweeping ? "Sweeping…" : "Run intel sweep"}
              </Button>
            )}
          </div>
        </div>

        {/* Summary cards row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mt-5">
          <Card label="Invoiced YTD" value={inr(summary.total_invoiced_ytd)} testid="sum-invoiced" />
          <Card label="Outstanding" value={inr(summary.total_outstanding)} tone={summary.total_outstanding > 0 ? "warning" : "success"} testid="sum-outstanding" />
          <Card label="Last payment" value={summary.latest_payment_date || "-"} testid="sum-last-payment" />
          <Card label="Latest NPS" value={summary.latest_nps ?? "-"} tone={summary.latest_nps >= 8 ? "success" : "warning"} testid="sum-nps" />
          <Card label="Latest CSAT" value={summary.latest_csat ?? "-"} tone={summary.latest_csat >= 4 ? "success" : "warning"} testid="sum-csat" />
          <Card label="Open actions" value={summary.open_actions} tone={summary.open_actions > 0 ? "warning" : "success"} testid="sum-open-actions" />
          <Card label="Days since MBR" value={summary.days_since_last_mbr ?? "-"} testid="sum-days-mbr" />
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue={initialTab}>
        <TabsList className="bg-white border border-slate-200 h-auto p-1">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="tab-campaigns">Campaigns ({campaigns.length})</TabsTrigger>
          <TabsTrigger value="contracts" data-testid="tab-contracts">Contracts ({contracts.length})</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews ({reviews.length})</TabsTrigger>
          <TabsTrigger value="profitability" data-testid="tab-profitability">Profitability</TabsTrigger>
          <TabsTrigger value="intelligence" data-testid="tab-intelligence">Intelligence ({intel.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          {/* KPI trends — show all KPIs across the client's campaigns */}
          <KpiTrendGrid kpiTrends={kpi_trends || []} />

          {/* NPS / CSAT trend */}
          <div className="mptg-card">
            <div className="mptg-overline">NPS & CSAT — last 12 reviews</div>
            <div className="h-56 mt-3">
              {reviews.length === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">No reviews yet</div>
              ) : (
                <ResponsiveContainer>
                  <BarChart data={[...reviews].reverse().slice(-12).map((r) => ({
                    m: r.period_month, NPS: r.nps_score ?? null, CSAT: r.csat_score ?? null,
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="m" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip contentStyle={{ background: "#1B2A4A", border: 0, color: "#fff", fontSize: 12 }} />
                    <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="NPS" fill="#2E86C1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="CSAT" fill="#1E8449" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Billing last 6 */}
          <div className="mptg-card p-0 overflow-hidden">
            <div className="p-4"><div className="mptg-overline">Last 6 months billing</div></div>
            <div className="overflow-x-auto">
              <table className="w-full mptg-table">
                <thead><tr><th>Month</th><th>Invoice #</th><th className="text-right">Invoiced</th><th className="text-right">Received</th><th className="text-right">Outstanding</th><th>Grade</th></tr></thead>
                <tbody>
                  {(billing_last_6 || []).map((b) => (
                    <tr key={b.id}>
                      <td>{b.month}</td>
                      <td className="font-mono text-xs">{b.invoice_number}</td>
                      <td className="text-right tabular-nums">{inr(b.invoice_amount)}</td>
                      <td className="text-right tabular-nums">{inr(b.amount_received)}</td>
                      <td className="text-right tabular-nums">{inr(b.outstanding_amount)}</td>
                      <td><GradeBadge grade={b.profitability_grade} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Latest MBR */}
          {latest_review && (
            <div className="mptg-card">
              <div className="mptg-overline">Latest {latest_review.review_type}</div>
              <div className="mt-2 flex items-center justify-between">
                <div>
                  <div className="font-heading text-lg font-bold">{latest_review.period_month}</div>
                  <div className="text-sm text-slate-600">{latest_review.review_date}</div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="badge-blue rounded-md px-2 py-0.5 text-xs font-semibold">NPS {latest_review.nps_score ?? "-"}</span>
                  <span className="badge-green rounded-md px-2 py-0.5 text-xs font-semibold">CSAT {latest_review.csat_score ?? "-"}</span>
                </div>
              </div>
              <div className="mt-3 text-sm text-slate-700">{latest_review.next_steps || "-"}</div>
              <Link to={`/reviews/${latest_review.id}`} className="text-xs text-[#2E86C1] hover:underline mt-2 inline-block">Open review →</Link>
            </div>
          )}
        </TabsContent>

        <TabsContent value="campaigns" className="mt-4">
          <CampaignsTab campaigns={campaigns} clientId={id} onChange={loadAll} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <ClientTimeline clientId={id} />
        </TabsContent>

        <TabsContent value="contracts" className="mt-4">
          <ContractsTab contracts={contracts} clientId={id} onCreated={loadAll} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="billing" className="mt-4">
          <BillingTab rows={billing} />
        </TabsContent>

        <TabsContent value="reviews" className="mt-4">
          <ReviewsTab reviews={reviews} clientId={id} />
        </TabsContent>

        <TabsContent value="profitability" className="mt-4">
          <ProfitabilityTab clientId={id} isAdmin={isAdmin} isBM={isBM} />
        </TabsContent>

        <TabsContent value="intelligence" className="mt-4">
          <IntelTab items={intel} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Card({ label, value, tone, testid }) {
  const tones = {
    success: "text-[#1E8449]",
    warning: "text-[#F39C12]",
    danger: "text-[#C0392B]",
  };
  return (
    <div className="border border-slate-200 rounded-md p-3" data-testid={testid}>
      <div className="mptg-overline">{label}</div>
      <div className={`font-heading text-lg font-bold mt-1 truncate ${tones[tone] || "text-slate-800"}`}>{value}</div>
    </div>
  );
}

function CampaignsTab({ campaigns, clientId, onChange, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users").then(({ data }) => setUsers(data));
  }, [isAdmin]);
  return (
    <div className="space-y-3">
      {isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="add-campaign-btn">
              <Plus className="w-4 h-4 mr-1" /> New campaign
            </Button>
          </DialogTrigger>
          <NewCampaignDialog clientId={clientId} users={users} onDone={() => { setOpen(false); onChange(); }} />
        </Dialog>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {campaigns.map((c) => (
          <Link key={c.id} to={`/campaigns/${c.id}`} className="mptg-card mptg-card-hover block">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="mptg-overline truncate">{c.type}</div>
                <div className="font-heading text-base font-bold truncate">{c.name}</div>
              </div>
              <StatusBadge status={c.status} />
            </div>
            <div className="text-xs text-slate-500 mt-1">Branch: {c.branch || "-"} · {c.kpi_count} KPIs</div>
            <div className="flex items-center gap-2 mt-3">
              <span className="badge-green rounded px-1.5 py-0.5 text-[11px] font-semibold">● {c.rag.green}</span>
              <span className="badge-amber rounded px-1.5 py-0.5 text-[11px] font-semibold">● {c.rag.amber}</span>
              <span className="badge-red rounded px-1.5 py-0.5 text-[11px] font-semibold">● {c.rag.red}</span>
              <span className="badge-slate rounded px-1.5 py-0.5 text-[11px] font-semibold">● {c.rag.no_data}</span>
            </div>
          </Link>
        ))}
        {campaigns.length === 0 && (
          <div className="mptg-card col-span-3 text-center text-slate-400 py-8 text-sm">No campaigns for this client yet.</div>
        )}
      </div>
    </div>
  );
}

function NewCampaignDialog({ clientId, users, onDone }) {
  const [f, setF] = useState({
    name: "", type: "Inbound Voice", branch_ids: [], region: "",
    process_manager_id: "", start_date: "", end_date: "", status: "active",
  });
  const [branches, setBranches] = useState([]);
  useEffect(() => { api.get("/branches").then(({ data }) => setBranches(data)); }, []);
  const toggle = (id) => setF((p) => ({
    ...p,
    branch_ids: p.branch_ids.includes(id) ? p.branch_ids.filter((x) => x !== id) : [...p.branch_ids, id],
  }));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name) { toast.error("Campaign name required"); return; }
    setSaving(true);
    try {
      await api.post("/campaigns", { client_id: clientId, ...f,
        process_manager_id: f.process_manager_id || null });
      toast.success("Campaign created");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <DialogContent className="max-w-xl" data-testid="new-campaign-dialog">
      <DialogHeader><DialogTitle className="font-heading">New campaign</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3 py-2">
        <div className="col-span-2"><Label>Campaign name *</Label>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="campaign-name-input" /></div>
        <div><Label>Type</Label>
          <Input value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} placeholder="Inbound Voice / Outbound Collections" /></div>
        <div><Label>Region</Label>
          <Input value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} placeholder="West" /></div>
        <div className="col-span-2">
          <Label>Delivery branches (one or more)</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {branches.map((b) => (
              <button key={b.id} type="button" onClick={() => toggle(b.id)}
                className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                  f.branch_ids.includes(b.id)
                    ? "bg-[#2E86C1] text-white border-[#2E86C1]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-[#2E86C1]"
                }`}
                data-testid={`campaign-branch-${b.code}`}
              >
                {b.code} · {b.name}
              </button>
            ))}
            {branches.length === 0 && <span className="text-xs text-slate-400">Create branches first in the Branches page.</span>}
          </div>
        </div>
        <div className="col-span-2"><Label>Process Manager</Label>
          <Select value={f.process_manager_id || "none"} onValueChange={(v) => setF({ ...f, process_manager_id: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="campaign-pm-select"><SelectValue placeholder="Pick PM" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— unassigned —</SelectItem>
              {users.filter((u) => u.role === "process_manager").map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-xs text-slate-500 mt-1">
            Can't find a PM / Branch Manager?{" "}
            <Link to="/admin" className="text-[#2E86C1] hover:underline font-medium">
              Add them in Admin → Users
            </Link>
            {" "}first, then come back.
          </div>
        </div>
        <div><Label>Start date</Label>
          <Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
        <div><Label>End date</Label>
          <Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></div>
        <div><Label>Status</Label>
          <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-campaign-btn">Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function ContractsTab({ contracts, clientId, onCreated, isAdmin }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3">
      {isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="add-contract-btn">
              <Plus className="w-4 h-4 mr-1" /> New version
            </Button>
          </DialogTrigger>
          <NewContractDialog clientId={clientId} onDone={() => { setOpen(false); onCreated(); }} />
        </Dialog>
      )}
      <div className="mptg-card p-0 overflow-hidden">
        <table className="w-full mptg-table">
          <thead><tr><th>Version</th><th>Period</th><th className="text-right">Value</th><th>Billing model</th><th>Status</th></tr></thead>
          <tbody>
            {contracts.map((c) => (
              <tr key={c.id}>
                <td className="font-mono">v{c.version_number}</td>
                <td>{c.start_date} → {c.end_date}</td>
                <td className="text-right tabular-nums">{inr(c.contract_value)}</td>
                <td className="capitalize">{(c.billing_model || "").replace("_", " ")}</td>
                <td>{c.is_current ? <span className="badge-green rounded px-2 py-0.5 text-xs font-semibold">Current</span> : <span className="badge-slate rounded px-2 py-0.5 text-xs font-semibold">Archived</span>}</td>
              </tr>
            ))}
            {contracts.length === 0 && (
              <tr><td colSpan={5} className="py-8 text-center text-slate-400">No contracts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewContractDialog({ clientId, onDone }) {
  const [f, setF] = useState({
    contract_value: 0, billing_model: "per_seat",
    start_date: "", end_date: "", renewal_alert_days: 60, notes: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/contracts", { client_id: clientId, ...f, pricing_details: {}, sla_definitions: {} });
      toast.success("Contract version created");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>New contract version</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3 py-2">
        <div><Label>Contract value (INR)</Label>
          <Input type="number" value={f.contract_value} onChange={(e) => setF({ ...f, contract_value: +e.target.value })} /></div>
        <div><Label>Billing model</Label>
          <Select value={f.billing_model} onValueChange={(v) => setF({ ...f, billing_model: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="per_seat">Per seat</SelectItem>
              <SelectItem value="per_transaction">Per transaction</SelectItem>
              <SelectItem value="per_hour">Per hour</SelectItem>
              <SelectItem value="hybrid">Hybrid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Start date</Label>
          <Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
        <div><Label>End date</Label>
          <Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></div>
        <div><Label>Renewal alert days</Label>
          <Input type="number" value={f.renewal_alert_days} onChange={(e) => setF({ ...f, renewal_alert_days: +e.target.value })} /></div>
        <div className="col-span-2"><Label>Notes</Label>
          <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3} /></div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white">Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function BillingTab({ rows }) {
  return (
    <div className="mptg-card p-0 overflow-hidden">
      <table className="w-full mptg-table">
        <thead><tr><th>Month</th><th>Invoice</th><th className="text-right">Invoiced</th><th className="text-right">Received</th><th className="text-right">Outstanding</th><th>Aging</th><th>Grade</th></tr></thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.id}>
              <td>{b.month}</td>
              <td className="font-mono text-xs">{b.invoice_number}</td>
              <td className="text-right tabular-nums">{inr(b.invoice_amount)}</td>
              <td className="text-right tabular-nums">{inr(b.amount_received)}</td>
              <td className="text-right tabular-nums">{inr(b.outstanding_amount)}</td>
              <td className="uppercase text-xs">{b.aging_bucket}</td>
              <td><GradeBadge grade={b.profitability_grade} /></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-400">No billing yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ReviewsTab({ reviews, clientId }) {
  return (
    <div className="space-y-3">
      <div>
        <Button asChild className="bg-[#2E86C1] hover:bg-[#2471A3] text-white">
          <Link to={`/reviews/new?client_id=${clientId}`} data-testid="record-review-btn">
            <Plus className="w-4 h-4 mr-1" /> Record MBR / QBR
          </Link>
        </Button>
      </div>
      <div className="mptg-card p-0 overflow-hidden">
        <table className="w-full mptg-table">
          <thead><tr><th>Type</th><th>Period</th><th>Date</th><th>NPS</th><th>CSAT</th><th>Open actions</th><th></th></tr></thead>
          <tbody>
            {reviews.map((r) => (
              <tr key={r.id}>
                <td><span className={`rounded px-2 py-0.5 text-xs font-semibold ${r.review_type === "QBR" ? "badge-blue" : "badge-slate"}`}>{r.review_type}</span></td>
                <td>{r.period_month}</td>
                <td>{r.review_date}</td>
                <td className="tabular-nums">{r.nps_score ?? "-"}</td>
                <td className="tabular-nums">{r.csat_score ?? "-"}</td>
                <td className="tabular-nums">{r.open_actions}</td>
                <td className="text-right"><Link to={`/reviews/${r.id}`} className="text-xs text-[#2E86C1] hover:underline">Open →</Link></td>
              </tr>
            ))}
            {reviews.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-400">No reviews yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function IntelTab({ items }) {
  if (items.length === 0) {
    return <div className="mptg-card text-center text-slate-400 py-10 text-sm">No intelligence items yet. Use "Run intel sweep" above.</div>;
  }
  return (
    <div className="space-y-2">
      {items.map((r) => (
        <div key={r.id} className="mptg-card mptg-card-hover">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <ClassificationBadge classification={r.classification} />
                <span className="text-xs text-slate-500">{r.source_name || "Source"}</span>
                <span className="text-xs text-slate-400">·</span>
                <span className="text-xs text-slate-500">{(r.created_at || "").slice(0, 10)}</span>
              </div>
              <h3 className="font-heading font-bold text-base mt-1.5 tracking-tight">{r.headline}</h3>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{r.summary}</p>
            </div>
            {r.source_url && (
              <a href={r.source_url} target="_blank" rel="noreferrer"
                 className="text-xs text-[#2E86C1] hover:underline whitespace-nowrap flex items-center gap-1">
                Source <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProfitabilityTab({ clientId, isAdmin, isBM }) {
  const canEdit = isAdmin || isBM;
  const [rows, setRows] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [grade, setGrade] = useState("Beta");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/clients/${clientId}/profitability`);
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const save = async () => {
    if (!month) { toast.error("Pick a month"); return; }
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/profitability`, { period_month: month, grade, notes });
      toast.success("Profitability grade recorded");
      setNotes("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      {canEdit && (
        <div className="mptg-card">
          <div className="mptg-overline">Record monthly profitability grade</div>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
            <div>
              <Label className="text-xs">Month</Label>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="profit-month" />
            </div>
            <div>
              <Label className="text-xs">Grade</Label>
              <Select value={grade} onValueChange={setGrade}>
                <SelectTrigger data-testid="profit-grade"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Alpha", "Beta", "Gama"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Notes</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reason / evidence" />
            </div>
            <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-profit-btn">
              {saving ? "Saving…" : "Save grade"}
            </Button>
          </div>
          <div className="text-xs text-slate-500 mt-2">
            Grade is set manually based on Branch Manager's understanding — there is no automatic logic. Monthly grade is independent of per-invoice grade on the Billing module.
          </div>
        </div>
      )}
      <div className="mptg-card p-0 overflow-hidden">
        <table className="w-full mptg-table">
          <thead><tr><th>Month</th><th>FY</th><th>Grade</th><th>Notes</th><th>Updated</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="font-mono text-xs">{r.period_month}</td>
                <td className="text-xs text-slate-500">{r.fy}</td>
                <td><GradeBadge grade={r.grade} /></td>
                <td className="text-sm">{r.notes || "-"}</td>
                <td className="text-xs text-slate-500">{(r.updated_at || r.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No profitability grades recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

